import type { QueryFunction, QueryKey } from '@tanstack/vue-query'

import { createQueryKeys } from '@lukemorales/query-key-factory'

import type { CollectionResponseMeta } from '~/types/api/v2'
import type { UseInfiniteQueryOptionsWithoutQueryFn, UseQueryOptionsWithoutQueryFn } from '~/types/util/tanstack-query'

import { type ParametersGetAll, type ParametersGetEntity, responseDataTypeName, useFetchContact } from './useFetchContact'

export type { ContactCompany, ContactPerson } from './useFetchContact'

export const queryKeys = createQueryKeys(responseDataTypeName, {
  all: ({
    workspaceId,
    params,
  }:
  {
    workspaceId: MaybeRef<string>
    params?: MaybeRef<ParametersGetAll>
  },
  ) => ({
    queryKey: [
      workspaceId,
      ...(params ? [params] : []),
    ],
  }),
  companies: ({
    workspaceId,
    params,
  }:
  {
    workspaceId: MaybeRef<string>
    params?: MaybeRef<ParametersGetEntity<'persons'>>
  },
  ) => ({
    queryKey: [
      workspaceId,
      ...(params ? [params] : []),
    ],
  }),
  companiesInfinite: ({
    workspaceId,
    params,
  }: {
    workspaceId: MaybeRef<string>
    params?: MaybeRef<ParametersGetEntity<'persons'>>
  }) => ({ queryKey: [workspaceId, ...(params ? [params] : [])] }),
  persons: ({
    workspaceId,
    params,
  }: {
    workspaceId: MaybeRef<string>
    params?: MaybeRef<ParametersGetEntity<'companies'>>
  }) => ({
    queryKey: [workspaceId, ...(params ? [params] : [])],
  }),
  personsInfinite: ({
    workspaceId,
    params,
  }: {
    workspaceId: MaybeRef<string>
    params?: MaybeRef<ParametersGetEntity<'companies'>>
  }) => ({
    queryKey: [workspaceId, ...(params ? [params] : [])],
  }),
  byUserId: ({
    workspaceId,
    userId,
  }: {
    workspaceId: MaybeRef<string>
    userId: MaybeRef<string>
  }) => ({
    queryKey: [workspaceId, userId],
  }),
})

const all = (
  {
    params,
    useQueryOptions = {},
  }: {
    params?: MaybeRef<ParametersGetAll> | undefined
    useQueryOptions?: UseQueryOptionsWithoutQueryFn
  } = {},
) => {
  const { getAll } = useFetchContact()
  const { activeWorkspaceId } = useAuth()

  return useQuery({
    ...queryKeys.all({
      workspaceId: activeWorkspaceId as MaybeRef<string>, // DEV-5806 remove casting */
      params,
    }),
    queryFn: () => getAll({
      params: unref(params),
      workspaceId: unref(activeWorkspaceId) ?? '',
    }),
    enabled: () => !!unref(activeWorkspaceId),
    ...useQueryOptions,
  })
}

const companies = (
  {
    params,
    useQueryOptions = {},
  }: {
    params?: MaybeRef<ParametersGetEntity<'persons'>>
    useQueryOptions?: UseQueryOptionsWithoutQueryFn
  } = {},
) => {
  const { getCompanies } = useFetchContact()
  const { activeWorkspaceId } = useAuth()

  return useQuery({
    ...queryKeys.companies({
      workspaceId: activeWorkspaceId as MaybeRef<string>, // DEV-5806 remove casting
      params,
    }),
    queryFn: ({ signal }) => getCompanies({
      params: unref(params),
      workspaceId: unref(activeWorkspaceId) ?? '',
      signal,
    }),
    enabled: () => !!unref(activeWorkspaceId),
    ...useQueryOptions,
  })
}

const companiesInfinite = ({
  params,
  useQueryOptions = {},
}: {
  params?: MaybeRef<ParametersGetEntity<'persons'>>
  useQueryOptions?: UseInfiniteQueryOptionsWithoutQueryFn
}) => {
  const { getCompanies } = useFetchContact()
  const { activeWorkspaceId } = useAuth()

  return useInfiniteQuery({
    ...queryKeys.companiesInfinite({
      workspaceId: activeWorkspaceId as MaybeRef<string>,
      params,
    }),
    queryFn: ({ pageParam }) => getCompanies({
      params: {
        ...unref(params),
        page: pageParam,
      },
      workspaceId: unref(activeWorkspaceId) ?? '',
    }),
    getNextPageParam: (previousPage) => {
      // Workaround for the fact that the API returns the pagination in a different format than the other v2-endpoints
      const pagination = ((previousPage?.meta as unknown as Record<string, unknown>)?.pagination ?? previousPage?.meta) as CollectionResponseMeta

      const currentPage = pagination?.current_page
      const lastPage = pagination?.last_page

      if (currentPage < lastPage)
        return (currentPage ?? 0) + 1

      return null
    },
    initialPageParam: 1,
    enabled: () => !!unref(activeWorkspaceId),
    ...useQueryOptions,
  })
}

const persons = (
  {
    params,
    useQueryOptions = {},
  }: {
    params?: MaybeRef<ParametersGetEntity<'companies'>>
    useQueryOptions?: UseQueryOptionsWithoutQueryFn
  } = {},
) => {
  const { getPersons } = useFetchContact()
  const { activeWorkspaceId } = useAuth()

  return useQuery({
    ...queryKeys.persons({
      workspaceId: activeWorkspaceId as MaybeRef<string>,
      params,
    }),
    queryFn: () => getPersons({
      params: unref(params),
      workspaceId: unref(activeWorkspaceId) ?? '',
    }),
    enabled: () => !!unref(activeWorkspaceId),
    ...useQueryOptions,
  })
}

const personsInfinite = ({
  params,
  useQueryOptions = {},
}: {
  params?: MaybeRef<ParametersGetEntity<'companies'>>
  useQueryOptions?: UseInfiniteQueryOptionsWithoutQueryFn
}) => {
  const { getPersons } = useFetchContact()
  const { activeWorkspaceId } = useAuth()

  return useInfiniteQuery({
    ...queryKeys.personsInfinite({
      workspaceId: activeWorkspaceId as MaybeRef<string>,
      params,
    }),
    queryFn: ({ pageParam }) => getPersons({
      params: {
        ...unref(params),
        page: pageParam,
      },
      workspaceId: unref(activeWorkspaceId) ?? '',
    }),
    getNextPageParam: (previousPage) => {
      // Workaround for the fact that the API returns the pagination in a different format than the other v2-endpoints
      const pagination = ((previousPage?.meta as unknown as Record<string, unknown>)?.pagination ?? previousPage?.meta) as CollectionResponseMeta

      const currentPage = pagination?.current_page
      const lastPage = pagination?.last_page

      if (currentPage < lastPage)
        return (currentPage ?? 0) + 1

      return null
    },
    initialPageParam: 1,
    enabled: () => !!unref(activeWorkspaceId),
    ...useQueryOptions,
  })
}

const byUserId = ({
  userId,
  useQueryOptions,
}: {
  userId: MaybeRef<string>
  useQueryOptions?: UseQueryOptionsWithoutQueryFn
}) => {
  const { getByUserId } = useFetchContact()
  const { activeWorkspaceId } = useAuth()

  return useQuery({
    ...queryKeys.byUserId({
      workspaceId: activeWorkspaceId as MaybeRef<string>,
      userId,
    }),
    queryFn: () => getByUserId({
      userId: unref(userId),
      workspaceId: unref(activeWorkspaceId) ?? '',
    }),
    enabled: () => !!unref(activeWorkspaceId) && !!unref(userId),
    ...useQueryOptions,
  })
}

const aborted = (signal: AbortSignal) =>
  signal.reason instanceof Error
    ? signal.reason
    : new DOMException('The operation was aborted.', 'AbortError')

const fetchCached = () => {
  const queryClient = useQueryClient()
  const { getAll, getCompanies, getPersons } = useFetchContact()

  const fetchWithSignal = <T>(
    queryKey: QueryKey,
    queryFn: QueryFunction<T>,
    signal?: AbortSignal,
  ) => {
    if (signal?.aborted)
      throw aborted(signal)

    const query = queryClient.fetchQuery({
      queryKey,
      queryFn,
    })

    if (!signal)
      return query

    // The query is shared by key, so an abort only rejects this caller.
    // Cancelling the query would also fail other callers awaiting the same key.
    return new Promise<T>((resolve, reject) => {
      const onAbort = () => reject(aborted(signal))
      signal.addEventListener('abort', onAbort, { once: true })

      query.then(resolve, reject).finally(() => {
        signal.removeEventListener('abort', onAbort)
      })
    })
  }

  return {
    getAll: ({
      workspaceId,
      params,
      signal,
    }: {
      workspaceId: string
      params?: ParametersGetAll
      signal?: AbortSignal
    }) => fetchWithSignal(
      queryKeys.all({ workspaceId, params }).queryKey,
      ({ signal: querySignal }) => getAll({
        workspaceId,
        params,
        signal: querySignal,
      }),
      signal,
    ),
    getCompanies: ({
      workspaceId,
      params,
      signal,
    }: {
      workspaceId: string
      params?: ParametersGetEntity<'persons'>
      signal?: AbortSignal
    }) => fetchWithSignal(
      queryKeys.companies({ workspaceId, params }).queryKey,
      ({ signal: querySignal }) => getCompanies({
        workspaceId,
        params,
        signal: querySignal,
      }),
      signal,
    ),
    getPersons: ({
      workspaceId,
      params,
      signal,
    }: {
      workspaceId: string
      params?: ParametersGetEntity<'companies'>
      signal?: AbortSignal
    }) => fetchWithSignal(
      queryKeys.persons({ workspaceId, params }).queryKey,
      ({ signal: querySignal }) => getPersons({
        workspaceId,
        params,
        signal: querySignal,
      }),
      signal,
    ),
  }
}

export const useQueryContact = {
  all,
  companies,
  persons,
  byUserId,
  personsInfinite,
  companiesInfinite,
  fetch: fetchCached,
}
